import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Button } from 'react-bootstrap';
import { DefaultTextCellRenderer } from 'Components/organisms/AgGridBase';
import type { PhishingCampaignRow } from './phishingCampaignsTable.types';

const textCellClass = 'text-text-high-emphasis';

export type GetPhishingCampaignColumnsParams = {
  label: (key: string, fallback: string) => string;
  onSend: (id: string) => void;
  statusFilterValues: readonly string[];
};

export function getPhishingCampaignColumns({
  label,
  onSend,
  statusFilterValues,
}: GetPhishingCampaignColumnsParams): ColDef<PhishingCampaignRow>[] {
  return [
    {
      colId: 'name',
      field: 'name',
      headerName: label('NAME', 'Name'),
      sortable: true,
      filter: false,
      flex: 5,
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass, emptyLabel: '-' },
    },
    {
      colId: 'status',
      field: 'status',
      headerName: label('STATUS', 'Status'),
      sortable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [...statusFilterValues],
        suppressMiniFilter: false,
      },
      maxWidth: 140,
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: {
        className: `text-capitalize ${textCellClass}`,
        emptyLabel: '-',
      },
    },
    {
      colId: 'sentAt',
      headerName: label('SENT_AT_LABEL', 'Sent at'),
      sortable: true,
      filter: false,
      minWidth: 160,
      valueGetter: (p) => p.data?.sentAt ?? '-',
      comparator: (_a, _b, na, nb) => (na.data?.sentAtMs ?? 0) - (nb.data?.sentAtMs ?? 0),
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass },
    },
    {
      colId: 'total',
      field: 'total',
      headerName: label('TOTAL_RECIPIENTS_LABEL', 'Total'),
      sortable: true,
      filter: false,
      width: 80,
      minWidth: 80,
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass },
    },
    {
      colId: 'sent',
      field: 'sent',
      headerName: label('SENT_RECIPIENTS_LABEL', 'Sent'),
      sortable: true,
      filter: false,
      width: 80,
      minWidth: 80,
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass },
    },
    {
      colId: 'opened',
      field: 'opened',
      headerName: label('OPENED_RECIPIENTS_LABEL', 'Opened'),
      sortable: true,
      filter: false,
      width: 80,
      minWidth: 80,
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass },
    },
    {
      colId: 'clicked',
      field: 'clicked',
      headerName: label('CLICKED_RECIPIENTS_LABEL', 'Clicked'),
      sortable: true,
      filter: false,
      width: 80,
      minWidth: 80,
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass },
    },
    {
      colId: 'reported',
      field: 'reported',
      headerName: label('REPORTED_RECIPIENTS_LABEL', 'Reported'),
      sortable: true,
      filter: false,
      width: 100,
      minWidth: 100,
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass },
    },
    {
      colId: 'scheduledAt',
      headerName: label('SCHEDULED_AT_LABEL', 'Scheduled at'),
      sortable: true,
      filter: false,
      minWidth: 160,
      valueGetter: (p) => p.data?.scheduledAt ?? '-',
      comparator: (_a, _b, na, nb) => (na.data?.scheduledAtMs ?? 0) - (nb.data?.scheduledAtMs ?? 0),
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass },
    },
    {
      colId: 'actions',
      headerName: label('ACTIONS_LABEL', 'Actions'),
      sortable: false,
      filter: false,
      suppressHeaderMenuButton: true,
      minWidth: 120,
      cellRenderer: (params: ICellRendererParams<PhishingCampaignRow>) => {
        const row = params.data;
        if (!row) {
          return null;
        }
        const isSent = row.status.toLowerCase() === 'sent';
        return !isSent ? (
          <Button variant="secondary" size="sm" onClick={() => onSend(row.id)}>
            {label('SEND_LABEL', 'Send')}
          </Button>
        ) : (
          <span className="text-text-low-emphasis">{label('SENT_LABEL', 'Sent')}</span>
        );
      },
    },
  ];
}
